import type { Board, Position } from '../board'
import type { Move, MoveFunction, Piece, PieceFactory } from './'
import { getBasePiece, getMove, getPiece, moveTypes } from './'
import type { Rook } from './rook'
import { isRook } from './rook'

// Helper function to check if path between two positions is blocked
const isPathBlocked = (piece: Piece, targetPos: Position, board: Board): boolean => {
  const dx = targetPos.x - piece.position.x
  const dy = targetPos.y - piece.position.y
  
  // Determine step direction
  const stepX = dx === 0 ? 0 : dx > 0 ? 1 : -1
  const stepY = dy === 0 ? 0 : dy > 0 ? 1 : -1
  
  // Check each square along the path (excluding start and end)
  let currentX = piece.position.x + stepX
  let currentY = piece.position.y + stepY
  
  while (currentX !== targetPos.x || currentY !== targetPos.y) {
    const tile = board[currentY]?.[currentX]
    if (tile?.piece) {
      return true // Path is blocked
    }
    currentX += stepX
    currentY += stepY
  }
  
  return false
}

export function isKing(value: King | Piece | null): value is King {
  return value?.type === `king`
}

const canCastleKing = (king: King, board: Board): Move[] => {
  if (king.hasMoved) return []
  
  // Check if king is currently in check (without using movesForPiece to avoid recursion)
  for (const tile of board.flat()) {
    if (!tile.piece || tile.piece.color === king.color) continue;
    // Check if this piece can capture the king directly
    const dx = king.position.x - tile.piece.position.x
    const dy = king.position.y - tile.piece.position.y
    
    let canCapture = false
    
    // Check based on piece type
    switch (tile.piece.type) {
      case 'pawn':
        // Pawns can only capture diagonally
        const pawnDirection = tile.piece.color === 'white' ? -1 : 1
        canCapture = Math.abs(dx) === 1 && dy === pawnDirection
        break
      case 'knight':
        canCapture = (Math.abs(dx) === 2 && Math.abs(dy) === 1) || (Math.abs(dx) === 1 && Math.abs(dy) === 2)
        break
      case 'bishop':
        canCapture = Math.abs(dx) === Math.abs(dy) && !isPathBlocked(tile.piece, king.position, board)
        break
      case 'rook':
        canCapture = (dx === 0 || dy === 0) && !isPathBlocked(tile.piece, king.position, board)
        break
      case 'queen':
        canCapture = ((Math.abs(dx) === Math.abs(dy)) || (dx === 0 || dy === 0)) && !isPathBlocked(tile.piece, king.position, board)
        break
      case 'king':
        canCapture = Math.abs(dx) <= 1 && Math.abs(dy) <= 1
        break
    }
    
    if (canCapture) {
      return [] // Can't castle while in check
    }
  }
  
  const possibleRookPositions: Move[] = []
  const rook = getPiece(board, {
    x: king.position.x + 3,
    y: king.position.y,
  })
  const rook2 = getPiece(board, {
    x: king.position.x - 4,
    y: king.position.y,
  })
  
  // Check kingside castling
  if (rook && isRook(rook) && !rook.hasMoved) {
    // Check if squares between king and rook are empty
    const square1 = getPiece(board, { x: king.position.x + 1, y: king.position.y })
    const square2 = getPiece(board, { x: king.position.x + 2, y: king.position.y })
    
    if (!square1 && !square2) {
      possibleRookPositions.push({
        capture: null,
        piece: king,
        type: moveTypes.castling,
        newPosition: { x: 6, y: king.position.y },
        steps: { x: 2, y: 0 },
        castling: {
          rook: rook as Rook,
          rookSteps: { x: -2, y: 0 },
          rookNewPosition: { x: 5, y: king.position.y },
        },
      })
    }
  }
  
  // Check queenside castling
  if (rook2 && isRook(rook2) && !rook2.hasMoved) {
    // Check if squares between king and rook are empty
    const square1 = getPiece(board, { x: king.position.x - 1, y: king.position.y })
    const square2 = getPiece(board, { x: king.position.x - 2, y: king.position.y })
    const square3 = getPiece(board, { x: king.position.x - 3, y: king.position.y })
    
    if (!square1 && !square2 && !square3) {
      possibleRookPositions.push({
        capture: null,
        piece: king,
        type: moveTypes.castling,
        newPosition: { x: 2, y: king.position.y },
        steps: { x: -2, y: 0 },
        castling: {
          rook: rook2 as Rook,
          rookSteps: { x: 3, y: 0 },
          rookNewPosition: { x: 3, y: king.position.y },
        },
      })
    }
  }
  
  return possibleRookPositions
}

export const kingMoves: MoveFunction<King> = ({
  piece,
  board,
  propagateDetectCheck,
}) => {
  const moves: Move[] = []

  for (const steps of KING_MOVES) {
    const move = getMove({ piece, board, steps, propagateDetectCheck })
    if (!move) continue
    moves.push(move)
  }

  const possibleCastles = canCastleKing(piece, board)

  return [...moves, ...possibleCastles]
}

export const createKing = ({ color, id, position }: PieceFactory): King => {
  return {
    hasMoved: false,
    ...getBasePiece({ color, id, type: `king`, position }),
  }
}

export type King = Piece & {
  hasMoved: boolean
}

const KING_MOVES: Position[] = [
  {
    x: 0,
    y: -1,
  },
  {
    x: 0,
    y: 1,
  },
  {
    x: -1,
    y: 0,
  },
  {
    x: 1,
    y: 0,
  },
  {
    x: -1,
    y: -1,
  },
  {
    x: 1,
    y: 1,
  },
  {
    x: -1,
    y: 1,
  },
  {
    x: 1,
    y: -1,
  },
]
